import { z } from 'zod'
import type { BoardAccess, ShareRole } from './access.js'
import { type BoardKind, type BoardSnapshot, type Placement, titleSchema } from './schemas.js'

/**
 * Projects group storymaps, mindmaps and workflows. Every item belongs to exactly one
 * project, and a project's members reach everything in it: the owner as owner, members
 * as editors or viewers. Board-level shares still work alongside, for one-off sharing.
 */

/** How a user reaches a project. The same three levels as a board. */
export type ProjectRole = BoardAccess

/** Where a board goes when nobody says which project, and the user owns none yet. */
export const DEFAULT_PROJECT_NAME = 'My project'

/** `POST /api/projects`: a client-generated id, so a lost response can be retried. */
export const projectCreateSchema = z.object({
  id: z.uuid(),
  name: titleSchema,
})

/** `PATCH /api/projects/:id`. */
export const projectRenameSchema = z.object({
  name: titleSchema,
})

/** What the project list shows: identity, how the asking user reaches it, and its size. */
export interface ProjectSummary {
  id: string
  name: string
  ownerId: string
  ownerName: string
  role: ProjectRole
  /** Not counting the owner. */
  memberCount: number
  /** Every item in the project, whether or not this user can open it. */
  itemCounts: Record<BoardKind, number>
  createdAt: string
  updatedAt: string
}

/** A project member, as the members dialog lists them. The owner is not a member row. */
export interface ProjectMember {
  userId: string
  email: string
  displayName: string
  role: ShareRole
}

export function totalItems(project: Pick<ProjectSummary, 'itemCounts'>): number {
  return project.itemCounts.storymap + project.itemCounts.mindmap + project.itemCounts.workflow
}

/**
 * `GET /api/boards/:id/labels`: every tag and structure title already used in the board's
 * project — story map cards and headings, and mindmap and workflow nodes — so a node or card
 * can pick one instead of retyping it. Each list is unique (ignoring case) and sorted.
 */
export interface ProjectLabels {
  tags: string[]
  groups: string[]
  columns: string[]
  swimlanes: string[]
  rows: string[]
}

export type LabelSource = { [K in keyof ProjectLabels]?: Iterable<string> }

/** The labels a story map uses: card tags and the titles of its groups, columns, swimlanes and rows. */
export function storymapLabels(snapshot: Pick<BoardSnapshot, 'columnGroups' | 'columns' | 'swimlanes' | 'rows' | 'cards'>): LabelSource {
  return {
    tags: snapshot.cards.flatMap((card) => card.tags),
    groups: snapshot.columnGroups.map((group) => group.title),
    columns: snapshot.columns.map((column) => column.title),
    swimlanes: snapshot.swimlanes.map((swimlane) => swimlane.title),
    rows: snapshot.rows.map((row) => row.title),
  }
}

/** The labels set on mindmap or workflow nodes. Both fields may be missing on a node stored before they existed. */
export function nodeLabels(nodes: Iterable<{ tags?: readonly string[]; placement?: Partial<Placement> }>): LabelSource {
  const labels = { tags: [] as string[], groups: [] as string[], columns: [] as string[], swimlanes: [] as string[], rows: [] as string[] }
  for (const node of nodes) {
    const placement = node.placement ?? {}
    labels.tags.push(...(node.tags ?? []))
    labels.groups.push(placement.group ?? '')
    labels.columns.push(placement.column ?? '')
    labels.swimlanes.push(placement.swimlane ?? '')
    labels.rows.push(placement.row ?? '')
  }
  return labels
}

export function mergeLabels(sources: Iterable<LabelSource>): ProjectLabels {
  const seen = { tags: new Map<string, string>(), groups: new Map<string, string>(), columns: new Map<string, string>(), swimlanes: new Map<string, string>(), rows: new Map<string, string>() }
  for (const source of sources) {
    for (const key of Object.keys(seen) as (keyof ProjectLabels)[]) {
      for (const value of source[key] ?? []) {
        const label = value.trim()
        const folded = label.toLowerCase()
        if (label && !seen[key].has(folded)) seen[key].set(folded, label)
      }
    }
  }
  const sorted = (map: Map<string, string>) => [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  return { tags: sorted(seen.tags), groups: sorted(seen.groups), columns: sorted(seen.columns), swimlanes: sorted(seen.swimlanes), rows: sorted(seen.rows) }
}
